import React, { useState, useEffect, useRef } from "react";
import { navigate } from "wouter/use-browser-location";

const initialStore: Record<string, string> = {
  "email-link-expiry": "Implemented email verification link expiry using Redis TTL for secure and timely validation.",
  "api-cache": "Cached API responses to reduce latency and improve scalability.",
  "tickethub-cache": "Cached ticket hub to make tickets available during hot show.",
  "bull-queue": "Used Bull queue with Redis to manage delayed email reminder jobs reliably.",
  "seat-cache": "Managed real-time seat availability caching for ticket booking.",
  "scheduler-jobs": "Handled scheduled background jobs and cache invalidation.",
};

export const RedisSimulatorCLI: React.FC = () => {
  const initialOutput = [
    "# Welcome to Ranjan’s Redis CLI Simulator",
    "# Type commands like SET, GET, KEYS, or GETALL",
    "# Example: GET bull-queue"
  ];
  const [store, setStore] = useState(initialStore);
  const [command, setCommand] = useState("");
  const [output, setOutput] = useState<string[]>(initialOutput);

  const outputEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    outputEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [output]);

  const runCommand = () => {
    if (!command.trim()) return;
  
    const parts = command.trim().split(/\s+/);
    const cmd = parts[0].toUpperCase();
    
    if (cmd === "EXIT") {
      setOutput((prev) => [...prev, `127.0.0.1:6379> ${command}`, "Bye!"]);
      setTimeout(() => {
        navigate("/");
      }, 1000); // brief delay for UX effect
      setCommand("");
      return;
    }
    
    // 10% chance of Redis failure (simulate connection issue)
    if (Math.random() < 0.1 && cmd !== "CLEAR") {
      setOutput((prev) => [
        ...prev,
        `127.0.0.1:6379> ${command}`,
        "❌ ERR failed to connect to Redis. Try again."
      ]);
      setCommand("");
      return;
    }
    if (cmd === "CLEAR") {
      setOutput(initialOutput);
    } else {
      setOutput((prev) => [...prev, `127.0.0.1:6379> ${command}`]);
  
      if (cmd === "SET" && parts.length >= 3) {
        const key = parts[1];
        const value = parts.slice(2).join(" ");
        setStore((prev) => ({ ...prev, [key]: value }));
        setOutput((prev) => [...prev, "OK"]);
      } else if (cmd === "GET" && parts.length === 2) {
        const key = parts[1];
        const val = store[key];
        setOutput((prev) => [...prev, val ?? "(nil)"]);
      } else if (cmd === "KEYS" && parts.length === 2) {
        const pattern = parts[1].replace("*", ".*");
        const keys = Object.keys(store).filter((k) =>
          new RegExp(`^${pattern}$`).test(k)
        );
        setOutput((prev) => [...prev.concat(keys.length ? keys  : "(empty list)") ]);
      } else if (cmd === "GETALL") {
        const keys = Object.keys(store);
        if (keys.length === 0) {
          setOutput((prev) => [...prev, "(empty list)"]);
        } else {
          keys.forEach((key) => {
            setOutput((prev) => [...prev, `${key} => ${store[key]}`]);
          });
        }
      } else {
        setOutput((prev) => [...prev, "ERR unknown command or wrong args"]);
      }
    }
  
    setCommand("");
  };
      
  return (
    <div
      className="bg-black text-green-400 font-mono rounded-lg shadow-lg p-6 flex flex-col"
      style={{ height: "100%", width: "100%" }}
    >
      <div
        className="flex-1 overflow-y-auto mb-4 whitespace-pre-wrap"
        style={{ fontSize: "0.9rem", lineHeight: 1.4 }}
      >
        {output.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
        <div ref={outputEndRef} />
      </div>
      <div className="flex">
        <span className="mr-2 text-green-600 select-none">$</span>
        <input
          type="text"
          className="flex-1 bg-black border border-green-700 focus:border-green-500 outline-none rounded px-3 py-2 text-green-400 caret-green-400"
          // placeholder=" KEYS * Redis command (e.g. GET api)"
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && runCommand()}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          style={{ minWidth: 0 }}
        />
      </div>
    </div>
  );
};

export default RedisSimulatorCLI;
